import { useQuery } from '@tanstack/react-query';
import { DECISION_SCHEMA_LABEL, PRIORITY_LABEL, type DecisionSchemaCode, type Priority } from '@planning/contracts';
import { EncabezadoPagina } from '@xiltrion/ui/bloques/encabezado-pagina';
import { EstadoVacio } from '@xiltrion/ui/bloques/estados';
import { SelectorBuscable, SelectorSimple } from '@xiltrion/ui/bloques/selectores';
import { Button } from '@xiltrion/ui/button';
import { Card, CardContent } from '@xiltrion/ui/card';
import { Icono } from '@xiltrion/ui/icono';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@xiltrion/ui/tabs';
import { useState } from 'react';
import { apiGet, apiPost } from '@/api/client';
import { avisarError, useProjects } from '@/api/hooks';
import type { DecisionRow, IntakeItem } from '@/api/types';
import { ConfidenceBadge, DecisionSuggestion, confidenceWord, valueLabel } from './DecisionSuggestion';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

/** Bandeja de triaje (solicitudes entrantes) y de sugerencias del motor de decisión (spec §6.1-A, §6.2). */
export function ReviewInbox() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: intake = [] } = useQuery({ queryKey: ['intake'], queryFn: () => apiGet<IntakeItem[]>('/intake') });
  const { data: pending = [] } = useQuery({ queryKey: ['decisions', 'pending'], queryFn: () => apiGet<DecisionRow[]>('/decisions/pending') });
  const suggestions = pending.filter((d) => d.subjectType !== 'intake');
  return (
    <div className="space-y-6 p-6">
      <EncabezadoPagina titulo="Bandeja" descripcion="Solicitudes por enrutar y sugerencias del motor de decisión por revisar. Nada se aplica sin confianza suficiente." />
      <Tabs defaultValue="intake">
        <TabsList variant="line">
          <TabsTrigger value="intake"><Icono nombre="mdi:inbox-arrow-down-outline" />Solicitudes ({intake.length})</TabsTrigger>
          <TabsTrigger value="suggestions"><Icono nombre="mdi:robot-outline" />Sugerencias ({suggestions.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="intake" className="space-y-3 pt-4">
          {intake.length === 0 && <EstadoVacio titulo="No hay solicitudes por enrutar" descripcion="Las que llegan por correo al buzón de un proyecto o por formulario aparecen aquí si el motor no tuvo confianza suficiente." borde />}
          {intake.map((i) => <IntakeCard key={i.id} item={i} onDone={(taskId) => { void qc.invalidateQueries({ queryKey: ['intake'] }); if (taskId) navigate(`/tareas/${taskId}`); }} />)}
        </TabsContent>
        <TabsContent value="suggestions" className="space-y-3 pt-4">
          {suggestions.length === 0 && <EstadoVacio titulo="Sin sugerencias pendientes" borde />}
          {suggestions.map((d) => (
            <div key={d.id} className="space-y-1">
              <Button variant="link" size="sm" className="h-auto px-0" onClick={() => navigate(d.subjectType === 'task' ? `/tareas/${d.subjectId}` : `/proyectos/${d.subjectId}`)}>Ver {d.subjectType === 'task' ? 'la tarea' : d.subjectType === 'project' ? 'el proyecto' : d.subjectType}</Button>
              <DecisionSuggestion decision={d} />
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function IntakeCard({ item, onDone }: { item: IntakeItem; onDone: (taskId?: string) => void }) {
  const { data: projects = [] } = useProjects();
  const d = item.decision;
  const suggested = (f: string) => d?.decisions.find((x) => x.field === f)?.value as string | undefined;
  const [projectId, setProjectId] = useState<string | null>(item.projectId ?? suggested('project') ?? null);
  const [sectionId, setSectionId] = useState<string | null>(suggested('section') ?? null);
  const [priority, setPriority] = useState<string | null>(suggested('priority') ?? null);
  const sections = d?.reasoning?.schema?.fields.find((f) => f.name === 'section')?.options ?? [];
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="flex flex-wrap items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-medium">{item.subject}</div>
            <div className="text-xs text-muted-foreground">{item.source === 'EMAIL' ? 'Correo' : 'Formulario'}{item.fromAddress ? ` de ${item.fromAddress}` : ''} · {new Date(item.createdAt).toLocaleString('es-CO')}</div>
          </div>
          {d && <ConfidenceBadge decision={d} />}
        </div>
        {item.body && <p className="line-clamp-4 text-sm whitespace-pre-wrap text-muted-foreground">{item.body}</p>}
        {d && d.status === 'SUCCESS' && <p className="text-sm">El motor sugiere: {d.decisions.map((x) => `${x.field === 'priority' ? 'prioridad' : x.field === 'section' ? 'sección' : 'proyecto'} ${confidenceWord(x.confidence)} «${x.field === 'priority' ? PRIORITY_LABEL[x.value as Priority] : valueLabel(d, x.field, x.value)}»`).join(', ')}.</p>}
        <div className="flex flex-wrap items-center gap-2">
          {!item.projectId && <SelectorBuscable value={projectId} onChange={setProjectId} options={projects.map((p) => ({ value: p.id, label: p.name }))} placeholder="Proyecto" className="h-8 w-56" />}
          {sections.length > 0 && <SelectorSimple size="sm" value={sectionId} onChange={setSectionId} options={sections} placeholder="Sección" className="w-48" />}
          <SelectorSimple size="sm" value={priority} onChange={setPriority} options={Object.entries(PRIORITY_LABEL).map(([value, label]) => ({ value, label }))} placeholder="Prioridad" limpiable className="w-40" />
          <Button size="sm" disabled={!projectId} onClick={() => void apiPost<{ id: string }>(`/intake/${item.id}/route`, { projectId, sectionId, priority }).then((t) => onDone(t.id), avisarError)}><Icono nombre="mdi:arrow-right" />Crear tarea</Button>
          <Button size="sm" variant="ghost" onClick={() => void apiPost(`/intake/${item.id}/dismiss`).then(() => onDone(), avisarError)}>Descartar</Button>
        </div>
        {d && <p className="text-xs text-muted-foreground">{DECISION_SCHEMA_LABEL[d.schemaCode as DecisionSchemaCode]} · {d.engine} {d.engineVersion}</p>}
      </CardContent>
    </Card>
  );
}
